import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { TUploadedFile } from '@onesource/schemas';
import { EImageVariant } from '@onesource/schemas';
import { useCreateMediaFileMutation, useGetMediaFilesQuery } from '@onesource/services';
import type { ProcessServerConfigFunction } from 'filepond';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdCheckCircle, MdImage, MdUpload } from 'react-icons/md';
import { DialogTitle } from '../dialogs/dialog-title';
import { FILE_FORMATS, type EMimeType } from '../file-uploader/file-uploader';
import { useGetByDevice } from '../hooks/use-responsive';
import { MediaFileDetails } from './media-file-details';
import { MediaGridSkeleton } from './media-grid-skeleton';
import { MediaPreviewDialog } from './media-preview-dialog';
import { MediaThumbnail } from './media-thumbnail';
import { MediaUploaderPanel } from './media-uploader-panel';
import type { TUploadMeta } from './process-handler';

export type TMediaManagerContentProps = {
  /** Currently selected file key */
  value?: string;
  /** Called when a file is selected */
  onSelect?: (key: string, publicUrl: string | null) => void;
  /** Called alongside onSelect with the full file object — used by wrappers that need file metadata */
  onSelectFile?: (file: TUploadedFile) => void;
  /** Called when cancel/close is requested (optional, used when embedded in a dialog) */
  onClose?: () => void;
  /** FilePond server process handler */
  processHandler: ProcessServerConfigFunction;
  /**
   * Explicit list of accepted MIME types. Defaults to images + PDF.
   * @example FILE_FORMATS.IMAGE
   * @example [EMimeType.JPEG, EMimeType.PNG, EMimeType.PDF]
   */
  allowedFormats?: EMimeType[];
  /** Whether to show the Select / Cancel action footer */
  showActions?: boolean;
  /**
   * Which CDN variant URL is returned when the user clicks Select.
   * Defaults to `original`.
   */
  selectVariant?: EImageVariant;
  /**
   * Which image size variants to upload. Passed to the uploader panel.
   * Defaults to all variants.
   */
  uploadVariants?: EImageVariant[];
  /**
   * Whether the variant checkboxes in the uploader are editable.
   * Defaults to false (read-only).
   */
  variantsEditable?: boolean;
  /** Aspect ratio for the crop tool (width / height). e.g. 1 for square. Defaults to free-form. */
  cropAspectRatio?: number;
  /** Number of file thumbnails to show per row in the uploader panel. Defaults to 1. */
  uploaderColumns?: number;
  /** When true, shows system-wide files and uploads are tagged as system assets. Only usable by admin/kit_organizer. */
  isSystemUpload?: boolean;
  /**
   * When true, shows a toggle to switch between All / System / Personal file views.
   * The API enforces that non-admin users cannot access system or all-files views.
   * Hide this for regular user-facing media pickers.
   */
  showScopeToggle?: boolean;
};

type TFileScope = 'personal' | 'system' | 'all';

export function MediaManagerContent({
  value,
  onSelect,
  onSelectFile,
  onClose,
  processHandler,
  allowedFormats = FILE_FORMATS.IMAGE_AND_PDF,
  showActions = true,
  selectVariant = EImageVariant.original,
  uploadVariants,
  variantsEditable,
  cropAspectRatio,
  uploaderColumns,
  isSystemUpload,
  showScopeToggle,
}: TMediaManagerContentProps) {
  const theme = useTheme();
  const { isMobile } = useGetByDevice();
  const { mutate: createMediaFile } = useCreateMediaFileMutation();
  const pendingSelectRef = useRef<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<TUploadedFile | null>(null);
  const [previewFile, setPreviewFile] = useState<TUploadedFile | null>(null);
  const [scope, setScope] = useState<TFileScope>(isSystemUpload ? 'system' : 'personal');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [page, setPage] = useState(1);

  const isSystemParam: boolean | 'all' | undefined = showScopeToggle
    ? scope === 'all'
      ? 'all'
      : scope === 'system'
    : isSystemUpload;

  const { data: pageData, isLoading } = useGetMediaFilesQuery({
    page,
    limit: 24,
    refetchOnMount: 'always',
    isSystem: isSystemParam,
  });

  const files = useMemo(
    () => (pageData?.data ?? []).filter(f => (allowedFormats as string[]).includes(f.mimeType)),
    [pageData, allowedFormats],
  );
  const totalPages = pageData ? Math.ceil(pageData.total / pageData.limit) : 1;

  // Sync selectedFile when files load and a value is passed
  useEffect(() => {
    if (value && files.length > 0) {
      setSelectedFile(files.find(f => f.key === value) ?? null);
    }
  }, [value, files]);

  // Auto-select newly uploaded file once it appears in the list
  useEffect(() => {
    if (!pendingSelectRef.current || files.length === 0) {
      return;
    }
    const match = files.find(f => f.key === pendingSelectRef.current);
    if (match) {
      setSelectedFile(match);
      pendingSelectRef.current = null;
    }
  }, [files]);

  const handleSelect = useCallback(() => {
    if (!selectedFile) {
      return;
    }
    const fullUrl = selectedFile.cdnUrls?.[selectVariant] ?? selectedFile.publicUrl;
    const pathname = fullUrl ? new URL(fullUrl).pathname : null;
    onSelect?.(selectedFile.key, pathname);
    onSelectFile?.(selectedFile);
  }, [selectedFile, onSelect, onSelectFile, selectVariant]);

  const handleUploaded = useCallback(
    (metaJson: string) => {
      try {
        const meta: TUploadMeta = JSON.parse(metaJson);
        createMediaFile(
          {
            originalName: meta.originalName,
            cdnUrls: meta.cdnUrls,
            mimeType: meta.mimeType,
            sizeBytes: meta.sizeBytes,
            isSystem: isSystemUpload ?? false,
          },
          {
            // Use the DB-assigned UUID (not the S3 key) to auto-select after the list refreshes
            onSuccess: record => {
              pendingSelectRef.current = record.id;
            },
          },
        );
      } catch {
        enqueueSnackbar('Failed to save file record', { variant: 'error' });
      }
    },
    [createMediaFile, isSystemUpload],
  );

  const handleClosePreview = useCallback(() => setPreviewFile(null), []);
  const handleNavigatePreview = useCallback((file: TUploadedFile) => setPreviewFile(file), []);

  const handleThumbnailSelect = useCallback((file: TUploadedFile) => {
    setSelectedFile(prev => (prev?.key === file.key ? null : file));
  }, []);

  const handleThumbnailPreview = useCallback((file: TUploadedFile) => {
    setPreviewFile(file);
  }, []);

  const selectedKey = selectedFile?.key ?? null;

  const fileGrid = useMemo(
    () =>
      files.map(file => (
        <Grid item key={file.key} xs={6} sm={4} md={2.4}>
          <MediaThumbnail
            file={file}
            isSelected={selectedKey === file.key}
            onSelect={handleThumbnailSelect}
            onPreview={handleThumbnailPreview}
          />
        </Grid>
      )),
    [files, selectedKey, handleThumbnailSelect, handleThumbnailPreview],
  );

  const borderColor = theme.palette.divider;

  return (
    <>
      <Box sx={{ display: 'flex', flex: '1 1 0', overflow: 'hidden' }}>
        {/* Left — file grid */}
        <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {(showScopeToggle || isMobile) && (
            <Box
              sx={{
                px: 2.5,
                pt: 1.5,
                pb: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {showScopeToggle && (
                <ToggleButtonGroup
                  value={scope}
                  exclusive
                  size="small"
                  onChange={(_e, v: TFileScope) => {
                    if (v) {
                      setScope(v);
                      setPage(1);
                      setSelectedFile(null);
                    }
                  }}
                >
                  <ToggleButton value="personal">My Uploads</ToggleButton>
                  <ToggleButton value="system">System</ToggleButton>
                  <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
              )}
              {isMobile && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MdUpload />}
                  onClick={() => setUploadDialogOpen(true)}
                  sx={{ ml: showScopeToggle ? 'auto' : 0 }}
                >
                  Upload
                </Button>
              )}
            </Box>
          )}
          <Box sx={{ flex: '1 1 0', overflow: 'auto', p: 2.5 }}>
            {isLoading ? (
              <MediaGridSkeleton />
            ) : files.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <MdImage size={48} color={theme.palette.action.disabled} />
                <Typography variant="body2">
                  {isMobile
                    ? 'No files yet. Tap Upload to add one.'
                    : 'No files yet. Upload one using the panel on the right.'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={1.5}>
                {fileGrid}
              </Grid>
            )}
          </Box>

          {totalPages > 1 && (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="small"
              />
            </Box>
          )}
        </Box>

        {!isMobile && (
          <>
            <Divider orientation="vertical" flexItem />

            {/* Right — upload + selected file details */}
            <Box sx={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <MediaUploaderPanel
                allowedFormats={allowedFormats}
                processHandler={processHandler}
                onUploaded={handleUploaded}
                imageVariants={uploadVariants}
                variantsEditable={variantsEditable}
                cropAspectRatio={cropAspectRatio}
                uploaderColumns={uploaderColumns}
              />

              {selectedFile && (
                <>
                  <Divider />
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.action.hover,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                      }}
                    >
                      File Details
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                    <MediaFileDetails file={selectedFile} />
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      {showActions && (
        <Box
          sx={{
            borderTop: `1px solid ${borderColor}`,
            px: 2.5,
            py: 1.5,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {selectedFile ? (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
              Selected: <strong>{selectedFile.filename}</strong>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ flex: 1 }}>
              No file selected
            </Typography>
          )}
          <Button onClick={handleSelect} variant="contained" disabled={!selectedFile} startIcon={<MdCheckCircle />}>
            Select
          </Button>
        </Box>
      )}

      {/* Mobile upload dialog */}
      {isMobile && (
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullScreen>
          <DialogTitle onClose={() => setUploadDialogOpen(false)}>Upload File</DialogTitle>
          <DialogContent>
            <MediaUploaderPanel
              allowedFormats={allowedFormats}
              processHandler={processHandler}
              onUploaded={key => {
                handleUploaded(key);
                setUploadDialogOpen(false);
              }}
              imageVariants={uploadVariants}
              variantsEditable={variantsEditable}
              cropAspectRatio={cropAspectRatio}
              uploaderColumns={uploaderColumns}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Lightbox — full-screen image preview */}
      <MediaPreviewDialog
        file={previewFile}
        onClose={handleClosePreview}
        files={files}
        onNavigate={handleNavigatePreview}
      />
    </>
  );
}
